import React, { useMemo } from 'react';
import { useScope } from '../contexts/ScopeContext';

/**
 * StoreSelector — header-strip dropdown for the active store.
 * Used once at the top of the app; every page reads the selection via useScope().
 *
 * Shows an enterprise dropdown alongside the store dropdown when the user has
 * access to more than one enterprise (super admins, enterprise admins with
 * multi-enterprise permissions).
 */
const StoreSelector: React.FC = () => {
  const { ready, loading, stores, enterprises, storeId, enterpriseId, setStoreId, setEnterpriseId } = useScope();

  const storesForEnterprise = useMemo(() => {
    if (!enterpriseId) return stores;
    return stores.filter((s) => !s.enterpriseId || s.enterpriseId === enterpriseId);
  }, [stores, enterpriseId]);

  if (!ready && loading) {
    return <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>Loading…</span>;
  }

  if (stores.length === 0 && enterprises.length === 0) {
    return null;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      {enterprises.length > 1 && (
        <select
          value={enterpriseId || ''}
          onChange={(e) => setEnterpriseId(e.target.value || null)}
          title="Enterprise"
          style={{
            padding: '0.35rem 0.5rem',
            borderRadius: 4,
            border: '1px solid #d1d5db',
            background: 'white',
            fontSize: '0.85rem',
            maxWidth: 220
          }}
        >
          {enterprises.map((e) => (
            <option key={e.enterpriseId} value={e.enterpriseId}>
              {e.name || e.enterpriseId}
            </option>
          ))}
        </select>
      )}
      {storesForEnterprise.length > 0 && (
        <select
          value={storeId || ''}
          onChange={(ev) => setStoreId(ev.target.value || null)}
          title="Store"
          style={{
            padding: '0.35rem 0.5rem',
            borderRadius: 4,
            border: '1px solid #d1d5db',
            background: 'white',
            fontSize: '0.85rem',
            maxWidth: 260
          }}
        >
          {!storeId && <option value="">— select store —</option>}
          {storesForEnterprise.map((s) => (
            <option key={s.storeId} value={s.storeId}>
              {s.name || s.storeCode || s.storeId}
            </option>
          ))}
        </select>
      )}
    </div>
  );
};

export default StoreSelector;
